import type { LinkProps } from '@tanstack/react-router'

/**
 * A route the code builds at runtime, narrowed to what `Link` accepts.
 *
 * The router types `to` as a union of the routes it knows, which is what makes
 * a typo in a literal a build error. A path assembled at runtime — the shadow
 * rewrite that maps a customer route onto the advisor's view of it, a redirect
 * target read out of the URL — names a real route but is a plain `string` by
 * the time it gets here, and the union cannot see that. This is the one place
 * that gap is crossed, so it is not crossed anywhere else by accident.
 */
export const asRoute = (path: string) => path as LinkProps['to']

/** The query of such a route, narrowed the same way. The router types `search`
 *  against the route `to` names, which a runtime path cannot tell it. */
export const asSearch = (search: Record<string, string> | undefined) =>
  search as LinkProps['search']

/**
 * A stored href split into the two halves `Link` takes.
 *
 * A notification keeps the address it was raised for as one string, and `Link`
 * wants the path and the query apart — `href` alone does not satisfy its types.
 * Splitting it here rather than at the bell keeps the cast in the one file that
 * owns it.
 */
export function asLink(href: string): {
  to: LinkProps['to']
  search: LinkProps['search']
} {
  const [path, query] = href.split('?')
  return {
    to: asRoute(path),
    search: asSearch(
      query ? Object.fromEntries(new URLSearchParams(query)) : undefined,
    ),
  }
}
